// /brokers/risk — broker exposure across the forest (SM-716, SM-621 phase
// 08a; the views of SM-768).
//
// Every broker that is neither DRAFT nor CLOSED, with its exposure in
// GOLD — exposure is external credit, which exists in GOLD only, and
// broker-service refuses any other currency here (SM-768 B7), so there
// is nothing to choose (SM-848): own and subtree exposure, collateral,
// and, for a node with its own limit, the limit, headroom and
// utilisation, flagged when it nears the warning threshold or is
// blocked. Each figure is broker-service's (GET /admin/brokers/exposure,
// BROKER_EXPOSURE_VIEW) — nothing is added up here, and nothing across
// currencies. A row opens the node's Risk tab; unresolved shortfalls are
// on the receivables queue.
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { keepPreviousData, useQueries, useQuery } from "@tanstack/react-query";

import { ExposureCurrencyNote } from "@/components/brokers/BrokerRiskPanel";
import { EXPOSURE_CURRENCY } from "@/components/brokers/governance";
import { canonToWire } from "@/components/brokers/receivables";
import { Badge } from "@/components/ui/Badge";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { CurrencyAmount } from "@/components/ui/CurrencyAmount";
import { EmptyState } from "@/components/ui/EmptyState";
import { ErrorPanel } from "@/components/ui/ErrorPanel";
import { Pagination } from "@/components/ui/Pagination";
import { Skeleton } from "@/components/ui/Skeleton";
import { useMyPerms } from "@/hooks/useMyPerms";
import { brokerKeys, fetchBrokerExposureList, fetchBrokerSummary } from "@/lib/broker-queries";
import type { ExposureView } from "@/lib/broker-types";
import { formatBps } from "@/lib/bps";
import { cn } from "@/lib/cn";
import { Perm } from "@/lib/perms";

const PAGE_SIZE = 25;

// No currency here: an old link's ?currency=RED is dropped, not obeyed —
// broker-service would refuse it.
interface Search {
  page?: number;
}

export const Route = createFileRoute("/_app/brokers/risk")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    page: Number.isFinite(Number(s.page)) && Number(s.page) > 0 ? Number(s.page) : undefined,
  }),
  component: BrokerRiskPage,
});

const th = "px-3 py-3 text-left text-2xs font-medium uppercase tracking-wide text-muted";
const td = "px-3 py-3 text-xs";

function BrokerRiskPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const perms = useMyPerms();
  const canView = perms.has(Perm.BrokerExposureView);
  const page = search.page ?? 0;
  const filter = { currency: EXPOSURE_CURRENCY, limit: PAGE_SIZE, offset: page * PAGE_SIZE };

  const q = useQuery({
    queryKey: brokerKeys.exposureList(filter),
    queryFn: () => fetchBrokerExposureList(filter),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
    enabled: canView,
  });
  const rows = q.data?.items ?? [];

  // Name each node: one cached read per distinct broker on the page, and
  // only for an operator who may read brokers at all.
  const canName = perms.has(Perm.BrokerView);
  const ids = [...new Set(rows.map((r) => r.broker_id))];
  const names = useQueries({
    queries: ids.map((id) => ({
      queryKey: brokerKeys.broker(id),
      queryFn: () => fetchBrokerSummary(id),
      enabled: canName,
      staleTime: 5 * 60_000,
      retry: false,
    })),
  });
  const nameOf = new Map<string, string>();
  names.forEach((n, i) => {
    const name = n.data?.display_name || n.data?.legal_name;
    if (name) nameOf.set(ids[i], name);
  });

  if (perms.loaded && !canView) {
    return (
      <EmptyState title="No access" description="You need the BROKER_EXPOSURE_VIEW permission to see broker exposure." />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Broker risk</h1>
        <p className="text-sm text-muted">
          Each broker&apos;s exposure against its collateral and limit. Exposure comes from third-party credit
          accounts, which trade in GOLD only. Unresolved shortfalls are on the receivables queue.
        </p>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <ExposureCurrencyNote />
        <Link to="/brokers/receivables" className="text-2xs text-neon hover:underline">
          Broker receivables
        </Link>
      </div>

      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle>Exposure</CardTitle>
          <span className="text-2xs text-muted">{q.data?.page.total ?? 0}</span>
        </CardHeader>
        <CardBody className="p-0">
          {q.isLoading || !perms.loaded ? (
            <div className="space-y-2 p-5">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          ) : null}
          {q.isError ? <ErrorPanel error={q.error} reset={() => void q.refetch()} /> : null}
          {q.data && rows.length === 0 ? (
            <p className="px-5 py-16 text-center text-sm text-muted">No active broker has exposure to show.</p>
          ) : null}
          {rows.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface-2">
                  <tr>
                    <th className={`${th} px-5`}>Broker</th>
                    <th className={th}>State</th>
                    <th className={`${th} text-right`}>Own exposure</th>
                    <th className={`${th} text-right`}>Subtree exposure</th>
                    <th className={`${th} text-right`}>Collateral</th>
                    <th className={`${th} text-right`}>Limit</th>
                    <th className={`${th} text-right`}>Headroom</th>
                    <th className={`${th} text-right`}>Utilisation</th>
                    <th className={`${th} text-right`}>Open receivables</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((v) => (
                    <RiskRow
                      key={v.broker_id}
                      v={v}
                      name={nameOf.get(v.broker_id)}
                      onOpen={() =>
                        void navigate({
                          to: "/brokers/$id",
                          params: { id: v.broker_id },
                          search: { tab: "risk" },
                        })
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {q.data && q.data.page.total > 0 ? (
            <Pagination
              page={q.data.page}
              currentPage={page}
              onPageChange={(p) => void navigate({ search: (s) => ({ ...s, page: p > 0 ? p : undefined }) })}
              itemLabel="entries"
            />
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}

function RiskRow({ v, name, onOpen }: { v: ExposureView; name?: string; onOpen: () => void }) {
  const amount = (s: string | undefined, muted = false) =>
    s ? (
      <CurrencyAmount currency={v.currency} value={canonToWire(s)} tone={muted ? "muted" : "default"} />
    ) : (
      <span className="text-muted">—</span>
    );
  const flagged = v.blocked || v.warning;
  return (
    <tr
      className={cn(
        "cursor-pointer border-b border-border/60 last:border-0 hover:bg-surface-2/60",
        v.blocked && "bg-danger/5",
      )}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen();
      }}
      tabIndex={0}
    >
      <td className={`${td} px-5`}>
        {name ? <div className="font-medium">{name}</div> : null}
        <div className="font-mono text-2xs text-muted">{v.broker_id}</div>
        {v.root_broker_id !== v.broker_id ? <div className="font-mono text-2xs text-muted">{v.root_broker_id}</div> : null}
      </td>
      <td className={td}>
        {v.blocked ? (
          <Badge tone="danger">Blocked</Badge>
        ) : v.warning ? (
          <Badge tone="warning">Near the limit</Badge>
        ) : (
          <Badge tone="success">Within limits</Badge>
        )}
      </td>
      <td className={`${td} text-right`}>{amount(v.own_exposure)}</td>
      <td className={`${td} text-right`}>{amount(v.subtree_exposure)}</td>
      <td className={`${td} text-right`}>{amount(v.collateral, true)}</td>
      <td className={`${td} text-right`}>{v.has_own_limit ? amount(v.limit) : <span className="text-2xs text-muted">No own limit</span>}</td>
      <td className={cn(`${td} text-right`, flagged && "font-semibold")}>{v.has_own_limit ? amount(v.headroom) : "—"}</td>
      <td className={cn(`${td} text-right`, v.blocked ? "text-danger" : v.warning ? "text-warning" : undefined)}>
        {v.has_own_limit ? formatBps(v.utilisation_bps) : "—"}
      </td>
      <td className={`${td} text-right font-mono`}>{v.receivables_open}</td>
    </tr>
  );
}
